import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
	DropdownAtom,
	GuidanceCallout,
	ModalAtom,
	TextFieldAtom,
} from "../../../../components/atoms/atom";
import { useLazyLookupUserByEmailQuery } from "../../../../store/services/adminUsers";
import { setting } from "../../../../store/services/index";
import { showToast } from "../../../../utils/method";

function AddUserModal(props) {
	const { show, onClose, onSubmit, mode, userInfo = {}, loading } = props;
	const { t } = useTranslation();


	// storing form fields
	const [firstName, setFirstName] = useState(userInfo?.firstName || "");
	const [lastName, setLastName] = useState(userInfo?.lastName || "");
	const [email, setEmail] = useState(userInfo?.email || "");
	const [phoneNumber, setPhoneNumber] = useState(userInfo?.phoneNumber || "");
	const [role, setRole] = useState(userInfo?.role?.id || "");
	const [company, setCompany] = useState(userInfo?.company?.id || "");
	// storing error obj
	const [errorObj, setErrorObj] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		role: "",
		company: "",
	});
	// storing dropdown options
	const [roleOptions, setRoleOptions] = useState([]);
	const [companyOptions, setCompanyOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	// True when a debounced lookup finds an existing account for the typed email.
	const [emailTaken, setEmailTaken] = useState(false);
	const [triggerEmailLookup] = useLazyLookupUserByEmailQuery();

	// Function to validate email format
	const isValidEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Function to validate phone number
	const isValidPhoneNumber = (phone) => {
		// Allow empty value (not required field)
		if (!phone || phone.trim() === '') return true;

		// International phone number format with optional country code
		// Accepts formats like: +491234567890, 01234 567890, 0123-456-7890
		const phoneRegex = /^(\+?\d{1,3}[-\s]?)?\(?\d{3,4}\)?[-\s]?\d{3,4}[-\s]?\d{3,4}$/;
		return phoneRegex.test(phone.trim());
	};

	const fetchDropDownOptions = async () => {
		try {
			setIsLoading(true);
			const roleResult = await setting.getRoles();
			setRoleOptions(
				(roleResult.items || []).map((role) => {
					return {
						label: t(`roles.types.${role.name}`, { defaultValue: role.name }),
						value: role.id,
					};
				}),
			);

			// Fetch companies
			const companyResult = await setting.getCompanies();
			setCompanyOptions(
				(companyResult.items || []).map((company) => {
					return {
						label: company.name,
						value: company.id,
					};
				}),
			);
		} catch (error) {
			showToast(error || t("errors.general"), { type: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchDropDownOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Debounced duplicate-email lookup: once a well-formed email is entered we
	// ask the backend whether an account already uses it, so the admin gets
	// feedback before submitting. A match against the user currently being
	// edited is ignored (it is their own address).
	useEffect(() => {
		if (!email || !isValidEmail(email)) {
			setEmailTaken(false);
			return;
		}
		const timer = setTimeout(async () => {
			try {
				const found = await triggerEmailLookup(email).unwrap();
				setEmailTaken(found?.id ? found.id !== userInfo?.id : false);
			} catch {
				// 404 (or any error) => no blocking duplicate.
				setEmailTaken(false);
			}
		}, 400);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [email]);

	const onAddUser = () => {
		let localErrorObj = {};
		if (!firstName) {
			localErrorObj.firstName = "users.error-first-name-required";
		}
		if (!lastName) {
			localErrorObj.lastName = "users.error-last-name-required";
		}
		if (!email) {
			localErrorObj.email = "users.error-email-required";
		} else if (!isValidEmail(email)) {
			localErrorObj.email = "users.error-invalid-email";
		} else if (emailTaken) {
			localErrorObj.email = "users.error-email-exists";
		}
		if (!role) {
			localErrorObj.role = "roles.error-role-name-required";
		}
		// Validate phone number if provided
		if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
			localErrorObj.phoneNumber = "users.error-invalid-phone";
		}

		setErrorObj({ ...localErrorObj });
		const hasError = Object.keys(localErrorObj).length;
		if (!hasError) {
			onSubmit({
				...userInfo,
				firstName,
				lastName,
				email,
				phoneNumber,
				role,
				company,
			});
		}
	};

	return (
		<ModalAtom
			title={t(mode === "add" ? "users.add-user" : "users.edit-user")}
			show={show}
			handleClose={onClose}
			handleSubmit={onAddUser}
			disabled={loading || isLoading}
			body={
				<Container>
					{mode === "add" && (
						<Row className="mb-3">
							<Col>
								<GuidanceCallout
									variant="info"
									message={t("users.global-user-note")}
								/>
							</Col>
						</Row>
					)}
					<Row>
						<Col>
							<TextFieldAtom
								type="text"
								label={t("form.first-name")}
								placeholder={t("form.first-name")}
								required
								value={firstName}
								maxLength={50}
								error={
									errorObj.firstName
										? t("users.error-first-name-required")
										: ""
								}
								onChange={(e) => {
									setFirstName(e.target.value);
									setErrorObj({
										...errorObj,
										firstName: e.target.value
											? ""
											: "users.error-first-name-required",
									});
								}}
							/>
						</Col>
						<Col>
							<TextFieldAtom
								type="text"
								label={t("form.last-name")}
								placeholder={t("form.last-name")}
								required
								maxLength={50}
								value={lastName}
								error={
									errorObj.lastName
										? t("users.error-last-name-required")
										: ""
								}
								onChange={(e) => {
									setLastName(e.target.value);
									setErrorObj({
										...errorObj,
										lastName: e.target.value
											? ""
											: "users.error-last-name-required",
									});
								}}
							/>
						</Col>
					</Row>
					<Row className="mt-4 mb-4">
						<Col>
							<TextFieldAtom
								type="email"
								label={t("form.email")}
								placeholder={t("form.email")}
								required
								maxLength={50}
								value={email}
								error={
									errorObj.email
										? t(errorObj.email)
										: emailTaken
											? t("users.error-email-exists")
											: ""
								}
								autoComplete="new-password"
								onChange={(e) => {
									setEmail(e.target.value);
									const emailValue = e.target.value;
									if (!emailValue) {
										setErrorObj({
											...errorObj,
											email: "users.error-email-required"
										});
									} else if (!isValidEmail(emailValue)) {
										setErrorObj({
											...errorObj,
											email: "users.error-invalid-email"
										});
									} else {
										setErrorObj({
											...errorObj,
											email: ""
										});
									}
								}}
							/>
						</Col>
						<Col>
							<TextFieldAtom
								type="tel"
								label={t("form.mobile-number")}
								placeholder={t("form.mobile-number")}
								value={phoneNumber}
								error={errorObj.phoneNumber ? t(errorObj.phoneNumber) : ""}
								onChange={(e) => {
									const phoneValue = e.target.value;
									setPhoneNumber(phoneValue);

									if (phoneValue && !isValidPhoneNumber(phoneValue)) {
										setErrorObj({
											...errorObj,
											phoneNumber: "users.error-invalid-phone"
										});
									} else {
										setErrorObj({
											...errorObj,
											phoneNumber: ""
										});
									}
								}}
								hint={t("users.mobile-number-hint")}
							/>
						</Col>
					</Row>
					<Row className="mt-4 mb-4">
						<Col>
							<DropdownAtom
								label={t("roles.role-name")}
								placeholder={t("roles.role-name")}
								value={role}
								required
								options={roleOptions}
								error={
									errorObj.role
										? t("roles.error-role-name-required")
										: ""
								}
								onChange={(e) => {
									setRole(e.target.value);
									setErrorObj({
										...errorObj,
										role: e.target.value
											? ""
											: "roles.error-role-name-required",
									});
								}}
							/>
						</Col>
						<Col>
							<DropdownAtom
								label={t("users.company")}
								placeholder={t("users.company")}
								value={company}
								required={false}
								options={companyOptions}
								error={
									errorObj.company
										? t("users.error-company-required")
										: ""
								}
								onChange={(e) => {
									setCompany(e.target.value);
									setErrorObj({
										...errorObj,
										company: "",
									});
								}}
							/>
						</Col>
					</Row>
				</Container>
			}
		/>
	);
}

export default AddUserModal;
